import { EnvelopeIcon, PencilIcon, PhoneIcon, TrashIcon } from "@heroicons/react/20/solid";
import Button from "components/buttons/Button";
import ButtonWithPopoverDots from "components/buttons/ButtonWithPopoverDots";
import { useState } from "react";

import Avatar from "~/components/Avatar";
import BreadcrumbsSection from "~/components/Breadcrumbs";
import Card from "~/components/Card";
import ContentWrapper from "~/components/ContentWrapper";
import MapDisplay from "~/components/displays/MapDisplay";
import FormInput from "~/components/formElements/FormInput";
import Label from "~/components/formElements/Label";
import Headline from "~/components/Headline";
import MailTo from "~/components/MailTo";
import MainContent from "~/components/mainContent/MainContent";
import HeroSection, { heroSectionBackgroundColor, HeroSectionHighlights } from "~/components/sections/HeroSection";
import Stats from "~/components/Stats";
import { appRoutes } from "~/constants/appRoutes.ts";
import type { ClientContactPersonType } from "~/modules/client/api/clientContactPerson/clientContactPersonTypes.ts";
import UpdateContactPersonSidebar from "~/modules/client/components/contact/components/UpdateContactSidebar";
import DeleteClientContactPersonModal from "~/modules/client/components/DeleteClientContactPersonModal";
import { formatDateWithGermanMonth } from "~/utils/dateAndTimeUtils.ts";

interface Props {
	clientContactPersonData: ClientContactPersonType;
	additionalData: {
		clientDisplayName: string;
		genderName: string;
		languageDisplayName: string;
		salesFunnelDisplayName: string;
		salesTypeDisplayName: string;
	};
}

const ContactDetailsView: React.FC<Props> = ({ clientContactPersonData, additionalData }) => {
	const {
		id,
		city,
		clientId,
		countryId,
		dateOfBirth,
		emailBusiness,
		emailPrivate,
		firstName,
		houseNumber,
		jobTitle,
		lastName,
		//managedBy,
		mobilePhone,
		nobilityTitle,
		phone,
		street,
		title,
		useInformalSalutation,
		zipCode,
	} = clientContactPersonData;

	const { clientDisplayName, genderName, languageDisplayName, salesFunnelDisplayName, salesTypeDisplayName } =
		additionalData;

	const [showUpdateForm, setShowUpdateForm] = useState(false);
	const [showDeletionModal, setShowDeletionModal] = useState(false);
	const showMap = !!street && !!houseNumber && !!zipCode && !!city && countryId;

	return (
		<MainContent>
			<BreadcrumbsSection
				className={heroSectionBackgroundColor}
				pages={[
					appRoutes.contacts(),
					{
						path: appRoutes.contact().path,
						displayName: `${firstName} ${lastName}`,
					},
				]}
			/>

			<HeroSection>
				<div className="w-full gap-2 md:flex md:items-center md:justify-between md:space-x-5">
					<div className="flex items-center space-x-5">
						<div className="shrink-0">
							<Avatar firstName={firstName}
									lastName={lastName}
									width="w-20"
									height="h-20" />
						</div>
						<div>
							<Headline type="h1">
								{title} {nobilityTitle} {firstName} {lastName}
							</Headline>
							<p className="text-sm font-medium text-primary-500 text-opacity-50">{jobTitle}</p>
						</div>
					</div>
					<div className="flex justify-between gap-2">
						{emailBusiness && (
							<Button
								theme="secondary"
								onClick={() => (window.location.href = `mailto:${emailBusiness}`)}
							>
								<EnvelopeIcon className="size-5 text-current"
											  aria-hidden="true" />
								Email (geschäftlich)
							</Button>
						)}
						{phone && (
							<Button theme="secondary"
									onClick={() => (window.location.href = `tel:${phone}`)}>
								<PhoneIcon className="size-5 text-current"
										   aria-hidden="true" />
								Anrufen
							</Button>
						)}
						<ButtonWithPopoverDots
							theme="dark"
							items={[
								{
									label: "bearbeiten",
									onClick: () => setShowUpdateForm(true),
									icon: PencilIcon,
								},
								{
									label: "löschen",
									onClick: () => setShowDeletionModal(true),
									icon: TrashIcon,
								},
							]}
						/>
					</div>
				</div>
			</HeroSection>
			<HeroSectionHighlights>
				<Stats
					fields={[
						{ label: "Unternehmen", element: clientDisplayName },
						{ label: "Sales Funnel", element: salesFunnelDisplayName },
						{ label: "Sales Type", element: salesTypeDisplayName },
						{ label: "Sprache", element: languageDisplayName },
						{ label: "Ansprache", element: useInformalSalutation ? "Du" : "Sie" },

					]}
				/>
			</HeroSectionHighlights>

			<div className="flex flex-col gap-y-8">
				<ContentWrapper className="grid w-full grid-cols-2 gap-x-2">
					<Card>
						<div className="grid grid-cols-2 gap-4">
							{[
								{
									label: "Email geschäftlich",
									value: emailBusiness ? <MailTo to={emailBusiness} /> : "n/a",
								},
								{ label: "Email privat", value: emailPrivate ? <MailTo to={emailPrivate} /> : "n/a" },
								{ label: "Telefon", value: phone || "n/a" },
								{ label: "Mobil", value: mobilePhone || "n/a" },
								{
									label: "Geburtstag",
									value: dateOfBirth ? formatDateWithGermanMonth(new Date(dateOfBirth)) : "n/a",
								},
								{ label: "Geschlecht", value: genderName || "n/a" },
							].map(({ label, value }, index) => (
								<FormInput key={`contacts-${index}`}
										   className="sm:col-span-1">
									<Label>{label}</Label>
									<div className="text-sm font-bold text-gray-900">{value}</div>
								</FormInput>
							))}
						</div>
					</Card>
					<Card>
						<div className="text-sm font-medium text-gray-500">Addresse</div>
						{showMap ? <MapDisplay address={{ street, houseNumber, zipCode, city, countryId }} /> : "n/a"}
					</Card>
				</ContentWrapper>
			</div>

			<UpdateContactPersonSidebar
				isOpen={showUpdateForm}
				setOpen={() => setShowUpdateForm(false)}
				contactPersonData={clientContactPersonData}
			/>

			<DeleteClientContactPersonModal clientId={clientId}
											clientContactPersonId={id}
											isOpen={showDeletionModal}
											onClose={() => setShowDeletionModal(false)} />


		</MainContent>
	);
};

export default ContactDetailsView;
